/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Snackbar from '@mui/material/Snackbar';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDatePicker from 'components/MDDatePicker';
import MDDateRange from 'components/MDDateRange';
import TabPanel from 'components/TabPanel/TabPanel';
import MDSelect from 'components/MDSelect';
import MDSelectGroup from 'components/MDSelectGroup';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import ReceivePo from 'layouts/receive/ReceivePo';
import ReceiveFixedCost from 'layouts/receive/ReceiveFixedCost';
import stepperName from './ReturnSupplierStatus';
import ReturnSupplierStepper from './ReturnSupplierStepper';
import ReturnSupplierList from './ReturnSupplierList';
import SearchRequest from './SearchRequest';
import SearchSupplier from './SearchSupplier';

const initAttr = {
  id: '',
  code: '',
  bill_no: '',
  branch_id: '',
  supplier_id: '',
  user_id: '',
  request_id: '',
  poauto_id: '0',
  location_id: '',
  location_name: '',
  image_id: '',
  date_shipping: '0000-00-00',
  date_due: '0000-00-00',
  deposit: '0.00',
  vat_id: '2',
  discount: '0.00',
  discount_value: '0.00',
  discount_type: 'B',
  discount_unit: 'Y',
  total: '',
  shipping: '',
  amount: '',
  vat: '',
  vat_value: '',
  amount_vat: '',
  nettotal: '',
  paid: '',
  balance: '',
  remark: '',
  warehouse_id: '',
  kind: 'WEB',
  print_no: '0',
  checkall: 'N',
  status: 'edit',
  created_at: '',
  updated_at: '',
  po_supplier: '',
  po_code: '',
  request_pr: 'N',
  poauto_code: '',
  cn_no: '',
  cn_total: '',
  type_id: '',
  excel: '',
  print: '',
  date_record: Func.dateNow(),
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL',
  period: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initRequest = {
  id: '0',
  code: '',
  branch_id: '0',
  supplier_id: '0',
  user_id: '0',
  type_id: '0',
  type_name: '',
  user_name: '',
  branch_name: '',
  vat_id: '',
  vat_name: '',
  copy_id: '0',
  vat_type: 'NO',
  date_record: '',
  remark: '',
  discount: '0.00',
  total: '0.00',
  vat: '0.00',
  nettotal: '0.00',
  paid: '0.00',
  balance: '0.00',
  status: 'edit',
  created_at: '',
  updated_at: '',
  status_icon: '',
  status_name: '',
  status_color: '',
};

const initSupplier = {
  id: '',
  code: '',
  name: '',
  taxid: '',
  branch: '',
  business: '',
  credit_day: '0',
  credit_money: '0.00',
  fax: '',
  address: '',
  province_id: '0',
  district_id: '0',
  subdistrict_id: '0',
  zipcode: '',
  branch_id: '0',
  tel: '',
  email: '',
  remark: '',
  image_id: '0',
  enable: 'Y',
  created_at: '',
  updated_at: '',
  company_name: '',
  province_name: '',
  district_name: '',
  subdistrict_name: '',
  vat_name: '',
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'supplier', name: stepperName.supplier.name },
  { id: 'receive', name: stepperName.receive.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'cancel', name: stepperName.cancel.name },
];

function ReturnSupplier() {
  const mod = 'return_supplier';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalDel, setModalDel] = useState(false);
  const [stepper, setStepper] = useState({});
  const [cboVat, setCboVat] = useState([]);
  const [cboType, setCboType] = useState([]);
  const [cboWarehouse, setCboWarehouse] = useState([]);
  const [image, setImage] = useState([]);
  const [photo, setPhoto] = useState({});
  const [modalPhoto, setModalPhoto] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [tab, setTab] = useState(0);
  const [request, setRequest] = useState(initRequest);
  const [supplier, setSupplier] = useState(initSupplier);
  const [warehouse, setWarehouse] = useState({ id: '', name: '' });
  const listRef = useRef();
  const tableRef = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center', width: 160 },
    { id: 'tb.code', label: t(`attr.code`), align: 'center', width: 120 },
    { id: 'tb.type_id', label: 'ประเภท', align: 'left' },
    { id: 'tb.warehouse_id', label: 'คลังเบิกของ', align: 'center' },
    { id: 'tb.request_id', label: 'ใบคืน', align: 'center' },
    { id: 'tb.supplier_id', label: 'ผู้จัดจำหน่าย', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left' },
    { id: 'tb.nettotal', label: 'จำนวนเงิน', align: 'right' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.created_at', label: 'วันรับของ', align: 'center' },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const scrollLeftRight = () => {
    if (tableRef.current.scrollLeft > 0) {
      tableRef.current.scrollLeft = 0;
    } else {
      tableRef.current.scrollLeft += 1000;
    }
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const viewList = () => {
    listRef.current.view();
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setNavigate(result.navigate);
        setStepper(result.stepper);
        setImage(result.image);
        setWarehouse({ id: result.data.warehouse_id, name: result.data.warehouse_name });

        if (result.request) setRequest(result.request);
        else setRequest(initRequest);

        if (result.supplier) setSupplier(result.supplier);
        else setSupplier(initSupplier);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const add = (item) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      supplier_id: item.supplier_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const openPhoto = (item) => {
    setPhoto(item);
    setModalPhoto(true);
  };

  const print = () => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: attr.id,
      lang: app.lang,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        // window.open(result.data.print, "_blank");
        setAttr({ ...attr, print: result.data.print, print_no: result.data.print_no });
        setModalPrint(true);
      }
    });
  };

  const copy = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/copy`, app.token, {
          id: id,
        }).then((result) => {
          if (result.status === 'ok') {
            window.location.replace(`/${mod}/${result.id}`);
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const edit = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          remark: attr.remark,
          date_record: attr.date_record,
          cn_no: attr.cn_no,
          type_id: attr.type_id,
          vat_id: attr.vat_id,
          warehouse_id: attr.warehouse_id,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            open();
            viewList();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          status: status,
        }).then((result) => {
          if (result.status === 'ok') {
            open();
            viewList();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const del = () => {
    setLoading(true);
    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const selectRequest = (item) => {
    FetchData('OPTIONS', `/${mod}/selectrequest`, app.token, {
      request_id: item.request_id,
      id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        open();
        viewList();
      }
    });
  };

  const selectSupplier = (item) => {
    FetchData('OPTIONS', `/${mod}/selectsupplier`, app.token, {
      supplier_id: item.supplier_id,
      id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        open();
        viewList();
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboVat(result.vat);
        setCboType(result.type);
        setCboWarehouse(result.warehouse);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const statusDisplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: '14px',
          padding: '0 10px',
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: 'nowrap',
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <SearchSupplier callback={add} />
          </Grid>
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              sx={{ minHeight: 400 }}
              ref={tableRef}
              onDoubleClick={scrollLeftRight}
            >
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{statusDisplay(row)}</TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell className='nowrap'>{row.type_name}</TableCell>
                      <TableCell className='nowrap'>{row.warehouse_name}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.request_code}
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.supplier_code}</MDBox>
                        <MDBox className='cut' width={150}>
                          {row.supplier_name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.user_name}
                      </TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.nettotal)}</TableCell>
                      <TableCell align='left'>{row.remark}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'supplier' || key === 'cancel';
        case 'supplier':
          return key === 'receive' || key === 'cancel';
        case 'receive':
          return key === 'success' || key === 'cancel';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={ReturnSupplierStepper}>
            {action(key) ? (
              <MDButton
                variant='gradient'
                color={stepperName[key].color}
                size='small'
                onClick={() => approve(key)}
              >
                <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === 'supplier') return 2;
      if (attr.status === 'receive') return 3;
      if (attr.status === 'success') return 4;
      if (attr.status === 'cancel') return 9;

      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('supplier')}
          {stepLevel('receive')}
          {stepLevel('success')}
          {stepLevel('cancel')}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfo = () => {
    const readOnly = attr.status === 'success' || attr.status === 'cancel';

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_record: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_record)}
            input={{
              label: 'วันที่บิล',
              variant: 'standard',
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='ประเภทการคืน'
            labelDefault={t(`element.please_select`)}
            data={cboType}
            value={attr.type_id}
            onChange={(e) => setAttr({ ...attr, type_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='เลขที่ใบลดหนี้'
            onChange={(e) => setAttr({ ...attr, cn_no: e.target.value })}
            value={attr.cn_no}
            variant='standard'
            inputProps={{
              maxLength: 50,
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='ภาษีมูลค่าเพิ่ม'
            labelDefault={t(`element.please_select`)}
            data={cboVat}
            value={attr.vat_id}
            onChange={(e) => setAttr({ ...attr, vat_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelectGroup
            label='คลังตัดสต็อก'
            data={cboWarehouse}
            value={warehouse || null}
            onChange={(e, v) => {
              e.preventDefault();
              setAttr({ ...attr, warehouse_id: v.id });
              return setWarehouse(v);
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={t(`attr.remark`)}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant='standard'
            multiline
            inputProps={{
              maxLength: 200,
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <MDTypography variant='h5' mb={3}>
              ใบคืนของ
            </MDTypography>
          </Grid>
          <Grid item xs={6}>
            {attr.status === 'paid' || attr.status === 'cancel' ? null : (
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='gradient' color='success' size='small' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </MDBox>
            )}
          </Grid>
        </Grid>

        {renderInfo()}
      </Paper>
    );
  };

  const renderReturn = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          ใบขอคืน &nbsp;
          {request.status_name === '' ? null : (
            <MDBadge
              badgeContent={request.status_name}
              variant='contained'
              color={request.status_color}
              size='md'
              container
            />
          )}
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='รหัส'
              value={request.code}
              variant='standard'
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                inputProps: {
                  sx: { minWidth: 80 },
                  readOnly: true,
                },
                endAdornment:
                  attr.status === 'edit' ? <SearchRequest callback={selectRequest} /> : null,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='วันที่คืน'
              value={request.date_record}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ประเภทการคืน'
              value={request.type_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='สาขาที่คืน'
              value={request.branch_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ผู้คืน'
              value={request.user_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุ'
              value={request.remark === '' ? '-' : request.remark}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const creditDisplay = (inp) => {
    if (inp === 'CASH') return 'เงินสด';

    return 'เครดิต';
  };

  const renderSupplier = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          ผู้จัดจำหน่าย
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={5} lg={4}>
            <MDInput
              label='รหัส'
              value={supplier.code}
              variant='standard'
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                inputProps: {
                  sx: { minWidth: 80 },
                  readOnly: true,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg={8}>
            <MDInput
              label='ชื่อ'
              value={supplier.name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <MDInput
              label='บริษัท'
              value={supplier.company_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='สาขา'
              value={supplier.branch}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ที่อยู่'
              value={`${supplier.address} ${supplier.subdistrict_name} ${supplier.district_name} ${supplier.province_name} ${supplier.zipcode}`}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='เบอร์โทรศัพท์'
              value={supplier.tel}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='E-mail'
              value={supplier.email}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='ภาษีมูลค่าเพิ่ม'
              value={supplier.vat_name}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='ประเภท'
              value={creditDisplay(supplier.credit_type)}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='เครดิต'
              value={supplier.credit_day}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>วัน</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MDInput
              label='วงเงิน'
              value={Func.numberDisplay(supplier.credit_money)}
              variant='standard'
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุ'
              value={supplier.remark === '' ? '-' : supplier.remark}
              variant='standard'
              multiline
              disabled
              fullWidth
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2}>
        {image.map((item) => (
          <Grid item xs={12} sm={12} md={6} lg={3} key={item.id}>
            <MDBox className='card-img'>
              <CardHeader
                title={`Photo ID : #${item.id}`}
                subheader={Func.dateTimeDisplay(item.created_at)}
              />
              <CardMedia
                sx={{ height: 300, mt: 0 }}
                image={item.image}
                title={item.id}
                onClick={() => openPhoto(item)}
              />
              <CardContent>
                <MDTypography variant='body2'>สร้างโดย : {item.user_name}</MDTypography>
              </CardContent>
            </MDBox>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        {attr.status === 'success' ? <ReceiveFixedCost callback={viewList} /> : null}
        <MDButton variant='gradient' color='warning' onClick={copy}>
          <Icon>content_copy</Icon>&nbsp;{t(`element.copy`)}
        </MDButton>
        <MDButton variant='gradient' color='info' onClick={print}>
          <Icon>print</Icon>&nbsp;{t(`element.print`)}{' '}
          {attr.print_no > 0 ? `(${attr.print_no})` : ''}
        </MDButton>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    aria-label='tabs-addedit'
                    sx={{ overflowX: 'scroll' }}
                  >
                    <Tab label={t(`element.info`)} />
                    {image.length > 0 ? <Tab label='ภาพการรับเข้า' /> : null}
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container mb={3} spacing={2}>
                    <Grid item xs={12} xl={6}>
                      <MDTypography variant='h3'>#{attr.code}</MDTypography>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderInfoEdit()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderReturn()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                      {renderSupplier()}
                    </Grid>
                    <Grid item xs={12}>
                      <MDBox className='card'>
                        <ReturnSupplierList openPhoto={openPhoto} ref={listRef} />
                      </MDBox>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  {renderImage()}
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {Func.dateTimeDisplay(photo.created_at)}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xl'
        keepMounted
        aria-describedby='dialog-print'
        fullScreen
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle>
          #{attr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {attr.print === '' ? null : (
            <Iframe
              url={attr.print}
              width='100%'
              height='100%'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.warehouse`), t(`menu.return_supplier`)]} />
      {renderPage()}
      {renderDelete()}
      {renderPhoto()}
      {renderPrint()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default ReturnSupplier;
